import React from 'react';
import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { RAG_TEXT, RAG_PILL, type RagBand } from '../../lib/reporting-format';
import { Sparkline } from './ChartPrimitives';

/**
 * Executive scorecard tile for the Overview tab. Each tile shows one headline
 * figure, an optional semantically-coloured delta, an optional sparkline, and
 * acts as a button that scrolls/links to the deep tab where the metric lives.
 *
 * Colour discipline: the value is always neutral foreground; only the delta /
 * band pill carries semantic colour (DESIGN.md §1). Tiles are real buttons so
 * they're keyboard-navigable (§16).
 */

interface ScoreTileProps {
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
  loading?: boolean;
  /** Sub-line under the value (e.g. "Worst: TEE-BLK-L"). */
  detail?: React.ReactNode;
  /** Signed delta string already formatted (e.g. "+$1,240"); colour from band. */
  delta?: string | null;
  deltaBand?: RagBand;
  /** Band pill text (e.g. "3.2 turns") rendered top-right when present. */
  bandLabel?: string;
  band?: RagBand;
  sparkline?: Array<{ date: string; units: number }>;
  /** Called on click / Enter — typically scrolls to the relevant deep tab. */
  onActivate?: () => void;
  activateLabel?: string;
}

export const ScoreTile: React.FC<ScoreTileProps> = ({
  label,
  value,
  icon: Icon,
  loading = false,
  detail,
  delta,
  deltaBand = 'neutral',
  bandLabel,
  band = 'neutral',
  sparkline,
  onActivate,
  activateLabel,
}) => {
  const DeltaArrow = deltaBand === 'bad' ? ArrowDownRight : ArrowUpRight;

  return (
    <button
      type="button"
      onClick={onActivate}
      aria-label={activateLabel}
      className={cn(
        'group flex w-full flex-col rounded-lg border bg-card p-4 text-left',
        'transition-colors duration-200 motion-reduce:transition-none',
        'hover:border-threadheads-purple/40 focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">{label}</p>
        {bandLabel ? (
          <span
            className={cn(
              'shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
              RAG_PILL[band],
            )}
          >
            {bandLabel}
          </span>
        ) : (
          <Icon
            className="h-4 w-4 text-muted-foreground/70 transition-colors group-hover:text-threadheads-purple"
            aria-hidden="true"
          />
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <p className="min-w-0 max-w-full truncate text-2xl font-bold tabular-nums leading-none">
            {value}
          </p>
        )}
        {delta && !loading && (
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold tabular-nums',
              RAG_TEXT[deltaBand],
            )}
          >
            <DeltaArrow className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {delta}
          </span>
        )}
      </div>

      {detail && !loading && (
        <p className="mt-1 text-xs text-muted-foreground truncate">{detail}</p>
      )}

      {sparkline && sparkline.length > 1 && !loading && (
        <Sparkline
          data={sparkline}
          className="mt-3"
          ariaLabel={`${label} trend`}
        />
      )}
    </button>
  );
};
